import { useState, useContext, useEffect } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Grid,
  Button
} from '@mui/material';
import { Correct } from '@abbott-components/icons';
import { InputButton } from '@abbott-components/core';
import {
  WorkflowEngineContext,
  WorkflowEngineActionKind,
  WorkflowEngineMode
} from '../WorkflowEngine/workflowEngineReducer';
import { useSnackbar } from 'notistack';
import { getFileUrl } from '../Shared/ajax';
import hasRoleAccessToPage from '../Shared/jsonSchemaUtilities/hasRoleAccessToPage';
import config from '../Shared/config';
import FileDisplayDialog from './FileDisplayDialog';

export default () => {
  const { state, dispatch } = useContext(WorkflowEngineContext);
  const {
    workflowData,
    workflowEntryExternalId,
    versionExternalId,
    workflowName,
    mode,
    pages,
    role
  } = state;

  const [isFileDisplayDialogOpen, setIsFileDisplayDialogOpen] =
    useState<boolean>(false);
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('');

  const [allPagesValid, setAllPagesValid] = useState<boolean>(false);

  useEffect(() => {
    const checkPagesValidity = () => {
      return pages.every((page) =>
        workflowData.some((data) =>
          Object.keys(data).some((key) => key.includes(`${page.id}-pageValid`))
        )
      );
    };

    setAllPagesValid(checkPagesValidity());
  }, []);

  const { enqueueSnackbar } = useSnackbar();

  const doneButtonHandler = () => {
    handleCloseDialog();

    if (config.workflowReturnUrl) {
      window.location.replace(config.workflowReturnUrl);
      return;
    }

    dispatch({
      type: WorkflowEngineActionKind.HIDE_COMPLETION_SCREEN
    });
  };

  const handleCloseDialog = () => {
    setIsFileDisplayDialogOpen(false);
  };

  const handleViewPrintClick = async () => {
    setIsFileDisplayDialogOpen(true);
    await loadPdfPreviewAsync();
  };

  const loadPdfPreviewAsync = async () => {
    try {
      let resultURL = '';

      if (mode === WorkflowEngineMode.ENTRY) {
        resultURL = await getFileUrl(
          `pdfDownload/entry/${workflowEntryExternalId}`
        );
      } else {
        let entryData: Record<string, unknown> = {};
        workflowData.forEach((d) => {
          entryData = { ...entryData, ...d };
        });

        resultURL = await getFileUrl(
          `pdfPreview/version/${versionExternalId}`,
          entryData
        );
      }

      setPreviewPdfUrl(resultURL);
    } catch (err) {
      enqueueSnackbar((err as Error)?.message, { variant: 'error' });
    }
  };

  useEffect(() => {
    const lastPage = pages[pages.length - 1];
    setHasAccess(hasRoleAccessToPage(mode, role, lastPage));
  }, []);

  return (
    <>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent sx={{ padding: 5 }}>
            <Stack sx={{ display: 'flex', alignItems: 'center' }} spacing={2}>
              <Correct sx={{ fontSize: '40px', color: '#00B140' }} />
              <Typography
                style={{
                  fontSize: '32px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  lineHeight: '1.2'
                }}
              >
                You did it! Great Job!
              </Typography>

              {hasAccess && allPagesValid ? (
                <Typography
                  style={{
                    fontSize: '22px',
                    textAlign: 'center'
                  }}
                >
                  Your tasks for this workflow are complete.
                  <br />
                  You may print a copy of the completed form for your records.
                </Typography>
              ) : (
                <Typography
                  style={{
                    fontSize: '22px',
                    textAlign: 'center'
                  }}
                >
                  Your tasks for this workflow are complete.
                </Typography>
              )}

              <Grid container direction="row" justifyContent="center">
                {hasAccess && allPagesValid ? (
                  <InputButton
                    name="changeViewButton"
                    type="button"
                    variant="text"
                    onClick={handleViewPrintClick}
                  >
                    View / Print
                  </InputButton>
                ) : null}
                <InputButton
                  name="doneButtonCompletionScreen"
                  type="button"
                  variant="contained"
                  onClick={doneButtonHandler}
                >
                  Done
                </InputButton>
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <FileDisplayDialog
        dialogOpen={isFileDisplayDialogOpen}
        dialogClose={handleCloseDialog}
        fileName={workflowName}
        fileUrl={`${previewPdfUrl}#toolbar=1&navpanes=0&zoom=100`}
        actionButton={
          <Button
            autoFocus
            name="doneButtonPrintView"
            color="inherit"
            onClick={doneButtonHandler}
          >
            Done
          </Button>
        }
      />
    </>
  );
};
